import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {
	Server,
	ServerInterface, ServerInterfaceNetwork, ServerInterfaceNetworkDeleteForm,
	ServerInterfaceNetworkForm
} from "../../../../../../../services/model/server.model";
import {distinctUntilChanged, finalize, forkJoin, Observable, of, Subscription, switchMap, tap} from "rxjs";
import {ServerDetailData, ServerDetailService} from "../../../../../../../services/front/server-detail.service";
import {ServerService} from "../../../../../../../services/back/server.service";
import {SnackbarService} from "../../../../../../../services/front/snackbar.service";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {Router} from "@angular/router";
import {MiniButtonModule} from "../../../../../../global/button/mini-button/mini-button.module";
import {CommonModule} from "@angular/common";
import {ButtonWidgetComponent} from "../../../../../../design-system/button-widget/button-widget.component";
import {DesignSystemModule} from "../../../../../../design-system/design-system.module";
import {Network, NetworkService} from "../../../../../../../services/back/network.service";
import {FormControl} from "@angular/forms";
import {filter} from "rxjs/operators";
import {MatMenu, MatMenuTrigger} from "@angular/material/menu";
import {MatDialog} from "@angular/material/dialog";
import {
	ServerInterfaceFormDialogComponent, ServerInterfaceFormDialogData
} from "./server-interface-form-dialog/server-interface-form-dialog.component";
import {IpCheckerService} from "../../../../../../../services/front/ip-checker.service";

@Component({
  selector: 'app-server-interfaces',
	imports: [
		TranslateModule,
		MiniButtonModule,
		CommonModule,
		ButtonWidgetComponent,
		DesignSystemModule,
		MatMenuTrigger,
		MatMenu
	],
  templateUrl: './server-interfaces.component.html',
  styleUrl: './server-interfaces.component.scss'
})
export class ServerInterfacesComponent implements OnInit, OnDestroy {
	tenantId: string;
	server: Server;
	isEditor: boolean;

	_initializing: boolean;
	_loading: boolean;
	_saving: boolean;
	initialized: boolean = false;

	networkList: Network[] = [];

	subscription: Subscription = new Subscription();
	formSubscription: Subscription = new Subscription();

	serverInterfaceNetworkControl: Map<string, FormControl<Network | null>> = new Map();

	private serverDetailService = inject(ServerDetailService);
	private serverService = inject(ServerService);
	private snackBarService = inject(SnackbarService);
	private translateService = inject(TranslateService);
	private router = inject(Router);
	private networkService = inject(NetworkService);
	private dialog = inject(MatDialog);
	private ipCheckerService = inject(IpCheckerService);

	ngOnInit() {
		this.subscription.add(this.serverDetailService.getInitializingChanges()
			.subscribe(initializing => this._initializing = initializing));
		this.subscription.add(this.serverDetailService.getServerDetailDataChanges()
			.pipe(tap(data => this.setServerDetailData(data)))
			.subscribe(() => this.initialize()));
	}

	setServerDetailData(data: ServerDetailData): void {
		this.tenantId = data.tenantId;
		this.server = data.server;
		this.isEditor = data.isEditor;
	}

	initialize(): void {
		this.switchLoading()
			.pipe(
				switchMap(() => this.networkService.getTenantNetworks(this.tenantId)),
				tap(networks => this.setData(networks)),
				finalize(() => this.switchLoading())
			).subscribe(() => this.initialized = true);
	}

	setData(networks: Network[]): void {
		this.networkList = networks;

		this.formSubscription.unsubscribe();

		this.formSubscription = new Subscription();

		this.serverInterfaceNetworkControl.clear();

		this.server.interfaces.forEach(serverInterface => {
			const control = new FormControl<Network | null>(serverInterface.network);
			this.serverInterfaceNetworkControl.set(serverInterface.serverInterface.ipAddress, control);

			this.formSubscription.add(control.valueChanges
				.pipe(
					distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
				).subscribe((data) => {
					const updatedInterface: ServerInterface = {
						serverId: serverInterface.serverInterface.serverId,
						ipAddress: serverInterface.serverInterface.ipAddress,
						isPrimary: serverInterface.serverInterface.isPrimary,
						networkId: data?.id || null
					}
					this.updateServerInterface(serverInterface.serverInterface, updatedInterface);
				}));
		})
	}

	updateServerInterface(previousInterface: ServerInterface, updatedInterface: ServerInterface): void {
		const form: ServerInterfaceNetworkForm = {
			previousIpAddress: previousInterface.ipAddress,
			ipAddress: updatedInterface.ipAddress,
			isPrimary: updatedInterface.isPrimary,
			networkId: updatedInterface.networkId,
		}

		this.switchLoading()
			.pipe(
				switchMap(() => this.serverService.createOrUpdateServerInterface(this.tenantId, this.server.id, form)),
				filter(success => success),
				tap(() => this.snackBarService.show(this.translateService.instant('page.application.detail.update.success'))),
				finalize(() => this.switchLoading())
			).subscribe(() => this.serverDetailService.refreshServer());
	}

	removeConnectedNetwork(serverInterface: ServerInterface): void {
		const control = this.serverInterfaceNetworkControl.get(serverInterface.ipAddress);
		if (control) {
			control.setValue(null);
		}
	}

	markInterfaceAsMain(serverInterface: ServerInterface): void {
		const form: ServerInterfaceNetworkForm = {
			previousIpAddress: serverInterface.ipAddress,
			ipAddress: serverInterface.ipAddress,
			isPrimary: true,
			networkId: serverInterface.networkId,
		}

		this.switchLoading()
			.pipe(
				switchMap(() => this.serverService.createOrUpdateServerInterface(this.tenantId, this.server.id, form)),
				filter(success => success),
				tap(() => this.snackBarService.show(this.translateService.instant('page.application.detail.update.success'))),
				finalize(() => this.switchLoading())
			).subscribe(() => this.serverDetailService.refreshServer());
	}

	deleteInterface(serverInterface: ServerInterface): void {
		const form: ServerInterfaceNetworkDeleteForm = {
			ipAddress: serverInterface.ipAddress
		}

		this.switchLoading()
			.pipe(
				switchMap(() => this.serverService.deleteServerInterface(this.tenantId, this.server.id, form)),
				filter(success => success),
				tap(() => this.snackBarService.show(this.translateService.instant('page.application.detail.update.success'))),
				finalize(() => this.switchLoading())
			).subscribe(() => this.serverDetailService.refreshServer());
	}

	openDialog(serverInterface?: ServerInterfaceNetwork): void {
		const data: ServerInterfaceFormDialogData = {
			serverId: this.server.id,
			serverInterface: serverInterface,
			alreadyExistingIpAddresses: this.server.interfaces
				.filter(i => i.serverInterface.ipAddress !== serverInterface?.serverInterface.ipAddress)
				.map(i => i.serverInterface.ipAddress)
		}
		this.dialog.open(ServerInterfaceFormDialogComponent, { position: { right: '162.5px' }, width: '475px', data: data})
			.afterClosed()
			.pipe(
				filter(success => success),
				tap(() => this.serverDetailService.refreshServer())
			).subscribe();
	}

	isIpInNetworkRange(serverInterface: ServerInterfaceNetwork): boolean {
		if (serverInterface.network)
			return this.ipCheckerService.isIpInRange(serverInterface.serverInterface.ipAddress, serverInterface.network.ipAddress, serverInterface.network.subnet);
		else
			return true;
	}

	switchLoading(): Observable<{}> {
		this._loading = !this._loading;
		return of({});
	}

	getControlForInterface(ip: string): FormControl<Network | null> {
		if (!this.serverInterfaceNetworkControl.has(ip)) {
			this.serverInterfaceNetworkControl.set(ip, new FormControl(null));
		}
		return this.serverInterfaceNetworkControl.get(ip)!;
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}
}
